// Nutzungsbalken für Stellen, Zugänge und Talentspeicher. Grün im Rahmen,
// gelb ab 80 %, rot ab voll. Die Zustände kommen aus LIMIT_STATES, damit
// Dashboard und Aivy OS dieselben Schwellen zeigen.
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { LIMIT_STATES, resolveLimitState } from '../../features/pricing/limits'
import { classNames } from '../../helpers'

const FILL = {
  [LIMIT_STATES.OK]: 'bg-green-500',
  [LIMIT_STATES.WARNING]: 'bg-yellow-400',
  [LIMIT_STATES.AT_LIMIT]: 'bg-red-500',
  [LIMIT_STATES.OVER_LIMIT]: 'bg-red-500'
}

const STATE_TEXT = {
  [LIMIT_STATES.WARNING]: 'text-yellow-800',
  [LIMIT_STATES.AT_LIMIT]: 'text-red-700',
  [LIMIT_STATES.OVER_LIMIT]: 'text-red-700',
  [LIMIT_STATES.PENDING]: 'text-gray-500',
  [LIMIT_STATES.UNKNOWN]: 'text-gray-500'
}

/** Zahl im Format der Oberfläche, 2.000 bzw. 2,000 */
export const formatCount = (value, language) => {
  if (value === null || value === undefined) return ''
  const locale = String(language || '')
    .toLowerCase()
    .startsWith('en')
    ? 'en'
    : 'de'
  return Number(value).toLocaleString(locale)
}

/**
 * Zustand und Füllung eines Meters, rein und ohne React. Gezeichnet wird nur
 * bei endlichem Limit und bekannter Nutzung (isMeter). Über dem Limit bleibt
 * die Füllung bei 100 %, ein Limit 0 gilt bei Nutzung 0 als voll.
 *
 * @param {{ used?: number|null, limit?: number|null }} params
 * @returns {{ state: string, used: number|null, limit: number|null,
 *   percent: number, fill: string|null, isMeter: boolean }}
 */
export const usageMeterModel = ({ used = null, limit = null } = {}) => {
  const resolved = resolveLimitState({ resource: null, limit, used })
  const isMeter = Object.prototype.hasOwnProperty.call(FILL, resolved.state)

  let percent = 0
  if (isMeter) {
    percent =
      resolved.limit > 0
        ? Math.min(100, (resolved.used / resolved.limit) * 100)
        : 100
  }

  return {
    state: resolved.state,
    used: resolved.used,
    limit: resolved.limit,
    percent,
    fill: FILL[resolved.state] || null,
    isMeter
  }
}

/**
 * Meter mit Text „x von y". size md zeigt das Label sichtbar über dem Balken
 * und den Zustand darunter, sm nur Balken und Zahl, das Label steht dann nur
 * in aria-valuetext. Bei unbegrenzt bleibt der Balken grau.
 */
const UsageMeter = ({ used, limit, label, size, failed, className }) => {
  const { t, i18n } = useTranslation()
  const model = usageMeterModel({ used, limit })
  const format = (value) => formatCount(value, i18n?.language)
  const isMd = size === 'md'

  let valueText
  // Ist das Zählen fehlgeschlagen, steht hier kein „Wird gezählt"
  if (failed && model.used === null) {
    valueText = t('pricing.usage.state_failed')
  } else if (model.isMeter) {
    valueText = t('pricing.usage.of', {
      used: format(model.used),
      limit: format(model.limit)
    })
  } else if (model.state === LIMIT_STATES.UNLIMITED) {
    valueText =
      model.used === null
        ? t('pricing.usage.unlimited')
        : t('pricing.usage.of_unlimited', { used: format(model.used) })
  } else if (model.state === LIMIT_STATES.PENDING) {
    valueText = t('pricing.usage.state_pending')
  } else {
    valueText =
      model.used === null
        ? t('pricing.usage.state_unknown')
        : format(model.used)
  }

  const meterProps = model.isMeter
    ? {
        role: 'meter',
        'aria-label': label || undefined,
        'aria-valuemin': 0,
        'aria-valuemax': model.limit,
        'aria-valuenow': Math.min(model.used, model.limit),
        'aria-valuetext': label
          ? t('pricing.usage.aria_value', {
              label,
              used: format(model.used),
              limit: format(model.limit)
            })
          : valueText
      }
    : {}

  // Ohne Limit und ohne Nutzung steht der Zustand schon im Wert
  const showStateText =
    isMd &&
    ([
      LIMIT_STATES.WARNING,
      LIMIT_STATES.AT_LIMIT,
      LIMIT_STATES.OVER_LIMIT
    ].includes(model.state) ||
      (model.state === LIMIT_STATES.UNKNOWN && model.used !== null))

  const track = (
    <div
      {...meterProps}
      data-state={model.state}
      className={classNames(
        'w-full overflow-hidden rounded-full bg-gray-200',
        isMd ? 'h-2' : 'h-1.5',
        model.state === LIMIT_STATES.PENDING && 'animate-pulse'
      )}
    >
      {model.isMeter && (
        <div
          className={classNames('h-full rounded-full', model.fill)}
          style={{ width: `${model.percent}%` }}
        />
      )}
    </div>
  )

  if (!isMd) {
    return (
      <div className={classNames('flex items-center gap-x-3', className)}>
        <div className='min-w-0 flex-1'>{track}</div>
        <span className='shrink-0 whitespace-nowrap text-xs tabular-nums text-gray-700'>
          {valueText}
        </span>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className='flex items-baseline justify-between gap-x-4 text-sm'>
        {label && <span className='font-medium text-gray-900'>{label}</span>}
        <span className='ml-auto whitespace-nowrap tabular-nums text-gray-700'>
          {valueText}
        </span>
      </div>
      <div className='mt-2'>{track}</div>
      {showStateText && (
        <p
          className={classNames(
            'mt-1.5 text-xs font-medium',
            STATE_TEXT[model.state]
          )}
        >
          {t(`pricing.usage.state_${model.state}`)}
        </p>
      )}
    </div>
  )
}

UsageMeter.propTypes = {
  used: PropTypes.number,
  limit: PropTypes.number,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
  failed: PropTypes.bool,
  className: PropTypes.string
}

UsageMeter.defaultProps = {
  used: null,
  limit: null,
  label: undefined,
  size: 'sm',
  failed: false,
  className: undefined
}

export default UsageMeter
